import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errText = (d) => (Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(" ") : d) || "Could not change password";

const PwField = ({ label, value, onChange, show, testId, autoComplete }) => (
  <label className="block">
    <span className="text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400">{label}</span>
    <input
      type={show ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      data-testid={testId}
      className="input mt-1.5"
      required
    />
  </label>
);

export default function SecurityPanel({ token }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;

  const submit = async (e) => {
    e.preventDefault();
    if (mismatch || tooShort) return;
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setError(errText(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="surface-card p-8 md:p-10" data-testid="security-panel">
      <p className="overline mb-2">Security</p>
      <h2 className="font-display text-2xl font-bold text-charcoal-600">Change password</h2>
      <p className="mt-2 text-sm text-charcoal-400 max-w-lg">
        Enter your current password, then choose a new one with at least 8 characters.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-4 max-w-md">
        <PwField label="Current password" value={current} onChange={setCurrent} show={show}
          testId="security-current-password" autoComplete="current-password" />
        <PwField label="New password" value={next} onChange={setNext} show={show}
          testId="security-new-password" autoComplete="new-password" />
        <PwField label="Confirm new password" value={confirm} onChange={setConfirm} show={show}
          testId="security-confirm-password" autoComplete="new-password" />
        <button type="button" onClick={() => setShow((s) => !s)} data-testid="security-toggle-show"
          className="inline-flex items-center gap-1.5 text-xs text-charcoal-500 hover:text-[#8B2252]">
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? "Hide" : "Show"} passwords
        </button>
        {(tooShort || mismatch || error) && (
          <p data-testid="security-error" className="text-sm text-red-600">
            {tooShort ? "New password must be at least 8 characters." : mismatch ? "New passwords don't match." : error}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4 pt-1">
          <button type="submit" disabled={saving || !current || !next || !confirm || mismatch || tooShort}
            data-testid="security-submit" className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update password
          </button>
          <Link to="/forgot-password" data-testid="security-forgot-link" className="text-sm text-[#8B2252] hover:underline underline-offset-4">
            Forgot your current password?
          </Link>
        </div>
      </form>
    </div>
  );
}
